import React, { useState, useEffect, useCallback } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { 
  User, Settings, ChevronDown, ChevronUp, Save, Check, X,
  Layers, Wrench, Search
} from 'lucide-react';
import { Input } from './ui/input';

const API = process.env.REACT_APP_BACKEND_URL;

export default function OperadorPermissoes({ operadorId, operadorNome, onClose, onSave }) {
  const [categorias, setCategorias] = useState([]);
  const [operacoes, setOperacoes] = useState([]);
  const [permissoes, setPermissoes] = useState({});
  const [categoriaExpandida, setCategoriaExpandida] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const carregarDados = useCallback(async () => {
    try {
      // Carregar categorias
      const resCat = await fetch(`${API}/api/categorias`);
      const dataCat = await resCat.json();
      setCategorias(dataCat);

      // Carregar operações
      const resOps = await fetch(`${API}/api/operacoes`);
      const dataOps = await resOps.json();
      // Normalizar dados: garantir que campos usados no frontend existem
      const opsNormalizadas = dataOps.map(op => ({
        ...op,
        categoria: op.categoria || op.categoria_nome || '',
        tempo_standard: op.tempo_standard || op.tempo_padrao_segundos || 0
      }));
      setOperacoes(opsNormalizadas);

      // Carregar permissões actuais do operador
      const resPerm = await fetch(`${API}/api/operadores/${operadorId}/permissoes`);
      const dataPerm = await resPerm.json();
      setPermissoes(dataPerm.permissoes_categorias || {});
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    }
    setLoading(false);
  }, [operadorId]);

  useEffect(() => {
    carregarDados();
  }, [carregarDados]);

  const toggleOperacao = (categoriaId, operacaoId) => {
    setPermissoes(prev => {
      const catPermissoes = prev[categoriaId] || [];
      const novasPermissoes = catPermissoes.includes(operacaoId)
        ? catPermissoes.filter(id => id !== operacaoId)
        : [...catPermissoes, operacaoId];
      
      return {
        ...prev,
        [categoriaId]: novasPermissoes
      };
    });
  };

  const toggleTodasCategoria = (categoriaId, categoriaNome) => {
    const opsCategoria = operacoes.filter(op => op.categoria_id === categoriaId || op.categoria === categoriaNome);
    const catPermissoes = permissoes[categoriaId] || [];
    const todasSeleccionadas = opsCategoria.every(op => catPermissoes.includes(op.id));
    
    setPermissoes(prev => ({
      ...prev,
      [categoriaId]: todasSeleccionadas ? [] : opsCategoria.map(op => op.id)
    }));
  };

  const salvarPermissoes = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API}/api/operadores/${operadorId}/permissoes`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ permissoes_categorias: permissoes })
      });
      
      if (res.ok) {
        if (onSave) onSave();
        if (onClose) onClose();
      }
    } catch (error) {
      console.error('Erro ao salvar permissões:', error);
    }
    setSaving(false);
  };

  const filtrarOperacoes = (ops) => {
    if (!searchTerm) return ops;
    return ops.filter(op => 
      op.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      op.codigo?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  };

  const contarPermissoes = (categoriaId) => {
    return (permissoes[categoriaId] || []).length;
  };

  const contarOperacoes = (categoriaId, categoriaNome) => {
    return operacoes.filter(op => op.categoria_id === categoriaId || op.categoria === categoriaNome).length;
  };

  if (loading) {
    return (
      <div className="p-6 flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-navy"></div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <Card className="w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-4 border-b bg-gradient-to-r from-navy to-navy/90 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-white/20 rounded-full p-2">
                <User className="h-6 w-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">Permissões de Operações</h2>
                <p className="text-white/80">{operadorNome}</p>
              </div>
            </div>
            <Button variant="ghost" size="sm" onClick={onClose} className="text-white hover:bg-white/20">
              <X className="h-5 w-5" />
            </Button>
          </div>
        </div>

        {/* Pesquisa */}
        <div className="p-4 border-b bg-stone-50">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-stone-400" />
            <Input
              placeholder="Pesquisar operação..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          
          {/* Atalhos de Categorias Personalizadas */}
          {categorias.filter(cat => cat.operacoes_ids && cat.operacoes_ids.length > 0).length > 0 && (
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-xs font-medium text-amber-700 mb-2">Adicionar permissões por categoria:</p>
              <div className="flex flex-wrap gap-2">
                {categorias
                  .filter(cat => cat.operacoes_ids && cat.operacoes_ids.length > 0)
                  .map((cat) => {
                    // Verificar quantas operações desta categoria já têm permissão
                    const opsIds = cat.operacoes_ids || [];
                    const permissoesAtivas = Object.values(permissoes).flat();
                    const numJaPermitidas = opsIds.filter(id => permissoesAtivas.includes(id)).length;
                    const todasPermitidas = numJaPermitidas === opsIds.length;
                    
                    return (
                      <button
                        key={`shortcut-${cat.id}`}
                        type="button"
                        onClick={() => {
                          // Adicionar/Remover todas as operações desta categoria personalizada
                          setPermissoes(prev => {
                            const novas = { ...prev };
                            
                            if (todasPermitidas) {
                              // Remover todas
                              for (const opId of opsIds) {
                                for (const catId of Object.keys(novas)) {
                                  novas[catId] = (novas[catId] || []).filter(id => id !== opId);
                                }
                              }
                            } else {
                              // Adicionar todas - colocar cada operação na sua categoria correta
                              for (const opId of opsIds) {
                                const op = operacoes.find(o => o.id === opId);
                                if (op) {
                                  const catOriginal = categorias.find(c => c.nome === op.categoria);
                                  if (catOriginal) {
                                    if (!novas[catOriginal.id]) novas[catOriginal.id] = [];
                                    if (!novas[catOriginal.id].includes(opId)) {
                                      novas[catOriginal.id].push(opId);
                                    }
                                  }
                                }
                              }
                            }
                            
                            return novas;
                          });
                        }}
                        className={`px-3 py-1.5 text-xs rounded-full border transition-colors flex items-center gap-1.5 ${
                          todasPermitidas 
                            ? 'bg-green-100 border-green-400 text-green-700' 
                            : 'hover:bg-amber-100'
                        }`}
                        style={{ borderColor: todasPermitidas ? undefined : cat.cor, color: todasPermitidas ? undefined : cat.cor }}
                      >
                        {todasPermitidas ? <Check className="h-3 w-3" /> : <Layers className="h-3 w-3" />}
                        {cat.nome}
                        <span className="opacity-60">({numJaPermitidas}/{opsIds.length})</span>
                      </button>
                    );
                  })}
              </div>
            </div>
          )}
        </div>

        {/* Conteúdo */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <p className="text-sm text-stone-600 mb-4">
            Seleccione as operações que este operador pode executar em cada categoria.
          </p>

          {categorias.map((cat) => {
            const opsCategoria = filtrarOperacoes(
              operacoes.filter(op => op.categoria_id === cat.id || op.categoria === cat.nome)
            );
            const numPermissoes = contarPermissoes(cat.id);
            const numTotal = contarOperacoes(cat.id, cat.nome);
            const isExpandida = categoriaExpandida === cat.id;
            const todasSeleccionadas = numPermissoes === numTotal && numTotal > 0;

            return (
              <Card key={cat.id} className="overflow-hidden">
                {/* Header da Categoria */}
                <div 
                  className={`p-3 cursor-pointer flex items-center justify-between ${
                    numPermissoes > 0 ? 'bg-green-50 border-l-4 border-green-500' : 'bg-stone-50'
                  }`}
                  onClick={() => setCategoriaExpandida(isExpandida ? null : cat.id)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${numPermissoes > 0 ? 'bg-green-100' : 'bg-stone-200'}`}>
                      <Layers className={`h-5 w-5 ${numPermissoes > 0 ? 'text-green-600' : 'text-stone-500'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold flex items-center gap-2">
                        <span className="text-xs font-mono bg-stone-200 px-1.5 py-0.5 rounded">
                          {cat.codigo}
                        </span>
                        {cat.nome}
                      </h3>
                      <p className="text-sm text-stone-500">
                        {numPermissoes} de {numTotal} operações permitidas
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {numPermissoes > 0 && (
                      <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">
                        {numPermissoes} seleccionadas
                      </span>
                    )}
                    {isExpandida ? <ChevronUp className="h-5 w-5" /> : <ChevronDown className="h-5 w-5" />}
                  </div>
                </div>

                {/* Lista de Operações */}
                {isExpandida && (
                  <div className="p-3 border-t bg-white">
                    {/* Seleccionar Todas */}
                    <div className="flex items-center justify-between mb-3 pb-3 border-b">
                      <span className="text-sm font-medium text-stone-600">
                        {opsCategoria.length} operações disponíveis
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => toggleTodasCategoria(cat.id, cat.nome)}
                      >
                        {todasSeleccionadas ? (
                          <>
                            <X className="h-4 w-4 mr-1" />
                            Desmarcar Todas
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4 mr-1" />
                            Seleccionar Todas
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Grid de Operações */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {opsCategoria.map((op) => {
                        const isSeleccionada = (permissoes[cat.id] || []).includes(op.id);
                        return (
                          <label
                            key={op.id}
                            className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                              isSeleccionada 
                                ? 'bg-green-100 border border-green-300' 
                                : 'bg-stone-50 border border-stone-200 hover:bg-stone-100'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSeleccionada}
                              onChange={() => toggleOperacao(cat.id, op.id)}
                              className="rounded"
                            />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${isSeleccionada ? 'text-green-800' : 'text-stone-700'}`}>
                                {op.nome}
                              </p>
                              {op.codigo && (
                                <p className="text-xs text-stone-500">{op.codigo}</p>
                              )}
                            </div>
                            {isSeleccionada && (
                              <Check className="h-4 w-4 text-green-600 flex-shrink-0" />
                            )}
                          </label>
                        );
                      })}
                    </div>

                    {opsCategoria.length === 0 && (
                      <p className="text-center text-stone-500 py-4">
                        Nenhuma operação encontrada
                      </p>
                    )}
                  </div>
                )}
              </Card>
            );
          })}

          {/* Secção Especial: Tarefas Pontuais (operações sem categoria) */}
          {(() => {
            const opsTarefas = filtrarOperacoes(
              operacoes.filter(op => op.apenas_tarefas === true || (!op.categoria && !op.categoria_id))
            );
            if (opsTarefas.length === 0) return null;
            
            const tarefasCatId = 'apenas-tarefas';
            const numPermissoesTarefas = (permissoes[tarefasCatId] || []).length;
            const isExpandida = categoriaExpandida === tarefasCatId;
            const todasSeleccionadas = numPermissoesTarefas === opsTarefas.length && opsTarefas.length > 0;
            
            return (
              <Card className="overflow-hidden border-purple-200">
                {/* Header */}
                <div 
                  className={`p-3 cursor-pointer flex items-center justify-between ${
                    numPermissoesTarefas > 0 ? 'bg-purple-50 border-l-4 border-purple-500' : 'bg-purple-50/50'
                  }`}
                  onClick={() => setCategoriaExpandida(isExpandida ? null : tarefasCatId)}
                >
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-lg ${numPermissoesTarefas > 0 ? 'bg-purple-100' : 'bg-purple-100/50'}`}>
                      <Layers className={`h-5 w-5 ${numPermissoesTarefas > 0 ? 'text-purple-600' : 'text-purple-400'}`} />
                    </div>
                    <div>
                      <h3 className="font-semibold flex items-center gap-2">
                        <span className="text-xs font-mono bg-purple-200 text-purple-700 px-1.5 py-0.5 rounded">
                          TAR
                        </span>
                        Tarefas Pontuais
                      </h3>
                      <p className="text-sm text-purple-600">
                        {numPermissoesTarefas} de {opsTarefas.length} operações permitidas
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    {numPermissoesTarefas > 0 && (
                      <span className="text-xs px-2 py-1 rounded-full bg-purple-100 text-purple-700">
                        {numPermissoesTarefas} seleccionadas
                      </span>
                    )}
                    {isExpandida ? <ChevronUp className="h-5 w-5 text-purple-600" /> : <ChevronDown className="h-5 w-5 text-purple-600" />}
                  </div>
                </div>

                {/* Lista de Operações */}
                {isExpandida && (
                  <div className="p-3 border-t bg-white">
                    {/* Seleccionar Todas */}
                    <div className="flex items-center justify-between mb-3 pb-3 border-b">
                      <span className="text-sm font-medium text-purple-600">
                        {opsTarefas.length} operações disponíveis
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        className="border-purple-300 text-purple-600 hover:bg-purple-50"
                        onClick={() => {
                          if (todasSeleccionadas) {
                            setPermissoes(prev => ({ ...prev, [tarefasCatId]: [] }));
                          } else {
                            setPermissoes(prev => ({ ...prev, [tarefasCatId]: opsTarefas.map(o => o.id) }));
                          }
                        }}
                      >
                        {todasSeleccionadas ? (
                          <>
                            <X className="h-4 w-4 mr-1" />
                            Desmarcar Todas
                          </>
                        ) : (
                          <>
                            <Check className="h-4 w-4 mr-1" />
                            Seleccionar Todas
                          </>
                        )}
                      </Button>
                    </div>

                    {/* Grid de Operações */}
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                      {opsTarefas.map((op) => {
                        const isSeleccionada = (permissoes[tarefasCatId] || []).includes(op.id);
                        return (
                          <label
                            key={op.id}
                            className={`flex items-center gap-2 p-2 rounded-lg cursor-pointer transition-colors ${
                              isSeleccionada 
                                ? 'bg-purple-100 border border-purple-300' 
                                : 'bg-purple-50/50 border border-purple-100 hover:bg-purple-50'
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={isSeleccionada}
                              onChange={() => {
                                setPermissoes(prev => {
                                  const current = prev[tarefasCatId] || [];
                                  if (current.includes(op.id)) {
                                    return { ...prev, [tarefasCatId]: current.filter(id => id !== op.id) };
                                  } else {
                                    return { ...prev, [tarefasCatId]: [...current, op.id] };
                                  }
                                });
                              }}
                              className="rounded accent-purple-600"
                            />
                            <div className="flex-1 min-w-0">
                              <p className={`text-sm font-medium truncate ${isSeleccionada ? 'text-purple-800' : 'text-stone-700'}`}>
                                {op.nome}
                              </p>
                              {op.codigo && (
                                <p className="text-xs text-purple-500">{op.codigo}</p>
                              )}
                            </div>
                            {isSeleccionada && (
                              <Check className="h-4 w-4 text-purple-600 flex-shrink-0" />
                            )}
                          </label>
                        );
                      })}
                    </div>
                  </div>
                )}
              </Card>
            );
          })()}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-stone-50 flex items-center justify-between">
          <p className="text-sm text-stone-500">
            Total: {Object.values(permissoes).flat().length} operações seleccionadas
          </p>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>
              Cancelar
            </Button>
            <Button onClick={salvarPermissoes} disabled={saving}>
              <Save className="h-4 w-4 mr-2" />
              {saving ? 'A guardar...' : 'Guardar Permissões'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
